import ReactDOM from 'react-dom';

import File from 'entities/File';

// For the mixin:
// The dependent mixin should have a getUploadURL function
// It is the url to post the file to. Any other attributes
// can be set in the handleSendingFile function
//
// Optional:
// handlers for events
//
// For the component:
// Dependencies: Requires a place for refs.dropzone and
// the attach button needs to have class .addFileButton.
// Can implement onUploadFailed to react to any errors
//
// Optional:
//  If you have a getDropzoneId function that returns a
//  unique string id, you can have multiple dropzones.
//  This is used for things like having saving the state
//  of a page and coming back, or showing multiple
//  dropzones on a single page.

const MAX_FILESIZE = 10 * 1024 * 1024;
const MAX_FILESIZE_NAME = '10MB';

const dropzones = [];

const stack = [];
let windowDragEventCount = 0;
let setUpMouseMoveListener = false;

// This is needed because mouse events still sometimes fire
//   when you’re dragging a file in chrome.
let mouseMoveClearTimeout = null;

const handleMouseMove = function() {
  if (!mouseMoveClearTimeout) {
    mouseMoveClearTimeout = setTimeout(() => {
      windowDragEventCount = 0;
      stack.forEach(s => s.handleMouseMoveWindow());

      if (setUpMouseMoveListener) {
        setUpMouseMoveListener = false;
        window.removeEventListener('mousemove', handleMouseMove);
      }
    }, 100);
  }
};

const handleDragMove = function() {
  if (mouseMoveClearTimeout) {
    clearTimeout(mouseMoveClearTimeout);
    mouseMoveClearTimeout = null;
  }
};

const handleDragEnter = function(event) {
  windowDragEventCount = Math.min(windowDragEventCount + 1, 2);
  stack.forEach(s => s.handleDragEnterWindow(event));

  if (!setUpMouseMoveListener) {
    setUpMouseMoveListener = true;
    window.addEventListener('mousemove', handleMouseMove, false);
  }
};

const handleDragLeave = function(event) {
  windowDragEventCount--;
  stack.forEach(s => s.handleDragLeave(event));
};

const handleDropFile = function(event) {
  event.preventDefault();
  windowDragEventCount = 0;
  stack.forEach(s => s.handleDropFile(event));
};

const FileDropzoneMixinSupport = {
  uploadFields: {},
  dragEventCount: 0,

  getInitialState() {
    return {
      fileEntities: {},
      files: [],
      fileData: {},
      isDraggingFile: false,
      isDraggingFileInner: false,
      dropzoneAttachFailed: false,
    };
  },

  _getDropzone() {
    let id = '_default';
    if (typeof this.getDropzoneId === 'function') {
      const dropzoneId = this.getDropzoneId();
      if (dropzoneId) {
        id = dropzoneId;
      }
    }
    if (!dropzones[id] && !this.state.dropzoneAttachFailed) {
      dropzones[id] = this._initializeDropzone();
    }
    return dropzones[id];
  },

  getFileEntities() {
    return this.state.files.map(file => this.state.fileEntities[file.key]);
  },

  getFileData(file) {
    if (!file) {
      return null;
    }

    return this.state.fileData[file.key];
  },

  _destroyDropzone() {
    let id = '_default';
    if (typeof this.getDropzoneId === 'function') {
      id = this.getDropzoneId();
    }
    if (dropzones[id]) {
      dropzones[id].destroy();
      dropzones[id] = undefined;
    }
  },

  _refreshEventListeners(dz) {
    const resolvedDz = dz || this._getDropzone();
    if (resolvedDz) {
      if (this.handleAcceptFile) {
        resolvedDz.options.accept = this.handleAcceptFile;
      }

      const refreshFcns = {
        addedfile: this.handleAddedFile,
        removedfile: this.handleRemovedFile,
        thumbnail: this.handleThumbnail,
        uploadprogress: this.handleUploadProgress,
        sending: this.handleSendingFile,
        success: this.handleUploadSuccess,
        error: this.handleUploadError,
        dragenter: this.handleDragEnterInner,
        dragleave: this.handleDragLeaveInner,
        drop: handleDropFile,
      };

      Object.keys(refreshFcns).forEach(key => {
        resolvedDz.off(key);
        if (refreshFcns[key]) {
          resolvedDz.on(key, refreshFcns[key]);
        }
      });
    }
  },

  _initializeDropzone() {
    if (!this.refs.dropzone_attach) {
      return undefined;
    }

    try {
      const Dropzone = require('dropzone');
      Dropzone.autoDiscover = false;

      const clickable = `addFileButton_${this.getDropzoneId()}`;
      const dropzone = new Dropzone(ReactDOM.findDOMNode(this.refs.dropzone), {
        url: this.getUploadURL,
        clickable: document.getElementsByClassName(clickable).length ? `.${clickable}` : false,
        paramName: 'file',
        maxFiles: 1,
        uploadMultiple: false,
        uploadprogress: this.handleUploadProgress,
        maxFilesize: MAX_FILESIZE / (1024 * 1024),
      });
      this._refreshEventListeners(dropzone);
      return dropzone;
    } catch (e) {
      if (e.message === 'Dropzone already attached.') {
        this.setState({ dropzoneAttachFailed: true });
        return undefined;
      } else {
        throw e;
      }
    }
  },

  getFiles() {
    return this.state.files;
  },

  getMaxFilesize() {
    return MAX_FILESIZE;
  },

  getMaxFilesizeName() {
    return MAX_FILESIZE_NAME;
  },

  destroyDropzoneIfEmpty() {
    if (this._getDropzone() && this.state.files.length === 0) {
      this._destroyDropzone();
    }
  },

  _removeWindowDragListeners() {
    window.removeEventListener('dragenter', handleDragEnter);
    window.removeEventListener('dragleave', handleDragLeave);
    window.removeEventListener('dragover', handleDragMove);
    window.removeEventListener('drop', handleDropFile);

    if (setUpMouseMoveListener) {
      setUpMouseMoveListener = false;
      window.removeEventListener('mousemove', handleMouseMove);
    }
  },

  initWindowDragListeners() {
    this._removeWindowDragListeners();

    // NOTE: The dragenter event must be registered on the window so that the drop
    // zone appears as soon as a file is dragged into the window. You can't do
    // 'this.dropzone.on('dragenter', this.handleDragEnter);' because it only
    // listens to events inside the dropzone element.
    window.addEventListener('dragenter', handleDragEnter, false);
    window.addEventListener('dragleave', handleDragLeave, false);
    window.addEventListener('dragover', handleDragMove, false);
    window.addEventListener('drop', handleDropFile, false);
  },

  componentWillMount() {
    if (stack.length === 0) {
      this.initWindowDragListeners();
    }

    stack.push(this);
  },

  componentDidMount() {
    this._getDropzone();

    this._refreshEventListeners();
  },

  componentDidUpdate() {
    if (this._getDropzone()) {
      // If the component updates we must re-bind the event handlers
      this._refreshEventListeners();
    }
  },

  componentWillUnmount() {
    this.destroyDropzoneIfEmpty();

    stack.pop();

    if (stack.length === 0) {
      this._removeWindowDragListeners();
    }
  },

  handleMouseMoveWindow() {
    // reset event counts
    this.dragEventCount = 0;

    this.setState({
      isDraggingFile: false,
      isDraggingFileInner: false,
    });
  },

  handleDragEnterWindow(event) {
    this.handleDragEnter(event);

    if (typeof this.onDragOverWindow === 'function') {
      this.onDragOverWindow();
    }
  },

  handleDragEnter(event) {
    event.preventDefault();

    this.setState({ isDraggingFile: true });
  },

  handleDragEnterInner(event) {
    windowDragEventCount = Math.min(windowDragEventCount + 1, 2);
    this.handleDragEnter(event);
    event.cancelBubble = true;

    this.dragEventCount = Math.min(this.dragEventCount + 1, 2);
    this.setState({ isDraggingFileInner: true });

    if (typeof this.onDragOverDropzone === 'function') {
      this.onDragOverDropzone();
    }
  },

  handleDragLeave(event) {
    event.preventDefault();

    if (windowDragEventCount === 0) {
      this.setState({ isDraggingFile: false });
    }
  },

  handleDragLeaveInner(event) {
    windowDragEventCount--;
    this.handleDragLeave(event);
    event.cancelBubble = true;

    this.dragEventCount--;
    // When dragEventCount is 0, this means the drag went out of
    // all elements and consequently, the window
    if (this.dragEventCount === 0) {
      this.setState({ isDraggingFileInner: false });
    }
  },

  handleDropFile(event) {
    event.preventDefault();
    this.dragEventCount = 0;
    this.setState({ isDraggingFile: false, isDraggingFileInner: false });
  },

  removeFile(file) {
    this.setState({ uploadedFile: null });
    this._getDropzone().removeFile(file);
    this.destroyDropzoneIfEmpty();
  },

  removeAllFiles() {
    if (this._getDropzone().removeAllFiles) {
      this._getDropzone().removeAllFiles();
      this.setState({ files: [], fileData: {} });
    }
    this.destroyDropzoneIfEmpty();
  },

  retryUpload(file) {
    this.removeFile(file);
    this._getDropzone().addFile(file);
  },

  resetDropzone() {
    this.removeAllFiles();
    this._getDropzone();
  },

  addFile(file) {
    const mockFile = {
      id: file.id,
      type: file.getContentType(),
      size: file.getSize(),
      name: file.getName(),
      urls: file.getUrls(),
    };

    const dz = this._getDropzone();
    dz.emit('addedfile', mockFile);
    this.setState({ uploadedFile: File.fromJSON(mockFile) });
    dz.emit('uploadprogress', mockFile, 100);
  },
};

export default FileDropzoneMixinSupport;



// WEBPACK FOOTER //
// ./src/lib/FileDropzoneMixinSupport.js